import * as React from 'react'
import {Button, Checkbox, Form} from "semantic-ui-react";
import {LogView} from "../../logView/LogView";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {
    FetchTransportOrderListFilterRequest,
    MultiOrderScriptingRequest,
    TransportOrder
} from "../../../../../generated";
import {TransportOrderTable} from "../../transportOrders/TransportOrderTable";
import AceEditor from "react-ace";
import {FilterConditions, TransportOrderFilterComponent} from "../../transportOrders/TransportOrderFilterComponent";
import {Filter} from "../../transportOrders/TransportOrderView";
import {ConfigContext} from "../../../context/ConfigContext";
import {RouteComponentProps} from "react-router";

export interface MultiOrderScriptEditProps extends RouteComponentProps<any> {
    updateXRequestId: (xRequestId) => void
    updateOriginalOrders: (originalOrders) => void
    history: any
}

export interface MultiOrderScriptEditState {
    isRunning: boolean
    validJson: boolean
    scriptExeToId: string
    xRequestId: string
    clientContext: boolean
    transportOrders: TransportOrder[]
    transportOrderAmount: number,
    selectedTransportOrderIds: string[],
    selectedTransportOrder: TransportOrder | null
    filter: FilterConditions
    sortBy: string,
    sortDirection: "ASC" | "DESC",
    topHeight: number,
    isDragging: boolean,
}

export class MultiOrderScriptEdit extends React.Component<MultiOrderScriptEditProps, MultiOrderScriptEditState> {

    static contextType = ConfigContext
    context!: React.ContextType<typeof ConfigContext>;

    containerRef = React.createRef<HTMLDivElement>();

    constructor(props) {
        super(props)

        this.state = {
            isRunning: false,
            validJson: true,
            scriptExeToId: "",
            xRequestId: "",
            clientContext: false,
            transportOrders: [],
            transportOrderAmount: 0,
            selectedTransportOrderIds: [],
            selectedTransportOrder: null,
            filter: new Filter(),
            sortBy: 'NAME',
            sortDirection: "DESC",
            topHeight: 0,
            isDragging: false,
        }
    }

    componentDidMount() {
        requestAnimationFrame(() => {
            if (this.containerRef.current) {
                this.setState({ topHeight: Math.round(this.containerRef.current.clientHeight * 0.67) })
            }
        })
    }

    componentDidUpdate(prevProps: Readonly<MultiOrderScriptEditProps>, prevState: Readonly<MultiOrderScriptEditState>, snapshot?: any) {

    }

    componentWillUnmount() {
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.onDragEnd);
    }

    onDragStart = (e: React.MouseEvent) => {
        e.preventDefault();
        this.setState({ isDragging: true });
        document.addEventListener('mousemove', this.onDragMove);
        document.addEventListener('mouseup', this.onDragEnd);
    }

    onDragMove = (e: MouseEvent) => {
        if (!this.containerRef.current) return;
        const rect = this.containerRef.current.getBoundingClientRect();
        const newTop = e.clientY - rect.top;
        this.setState({ topHeight: Math.max(150, Math.min(rect.height - 100, newTop)) });
    }

    onDragEnd = () => {
        this.setState({ isDragging: false });
        document.removeEventListener('mousemove', this.onDragMove);
        document.removeEventListener('mouseup', this.onDragEnd);
    }

    drawFilters() {
        let initialFilter = {} as FilterConditions

        return <TransportOrderFilterComponent
            onFilterChange={(fc: FilterConditions, reset?: boolean) => {
                this.setState({ filter: fc }, this.fetchTransportOrders)
            }}
            history={this.props.history}
            initialFilter={initialFilter}
            exportButtonVisible={true}
            partnerSelectionVisible={true}
            statusFilterVisible={true}
            location={this.props.location}
            match={this.props.match}
        />
    }

    async fetchTransportOrders() {
        console.log("fetching transportOrders")
        let auth = await backend.withTokenAuthHeader(authentication.token)

        let begin = this.state.filter.fromDateTime
        let dateTimeField
        let end = this.state.filter.toDateTime
        let exactSearchTermMatch
        let modifiedSince
        let partnerIds
        let partnerNameRegex
        let searchTerm = this.state.filter.term
        let skip
        let sortBy = this.state.sortBy
        let sortOrder = this.state.sortDirection
        let status = this.state.filter.status
        let take
        let validationStatus
        let vehicle

        let request = {} as FetchTransportOrderListFilterRequest
        request.companyId = this.context.companyId

        request.begin = begin
        request.dateTimeField = dateTimeField
        request.end = end
        request.exactSearchTermMatch = exactSearchTermMatch
        request.modifiedSince = modifiedSince
        request.partnerIds = partnerIds
        request.partnerNameRegex = partnerNameRegex
        request.searchTerm = searchTerm
        request.skip = skip
        request.sortBy = sortBy
        request.sortOrder = sortOrder
        //request.status = status
        request.take = take
        request.validationStatus = validationStatus
        request.vehicle = vehicle

        let response = await backend.transportOrderApi.fetchTransportOrdersWithFilter(
            request,
            auth
        )

        this.setState({ transportOrders: response.transportOrders, transportOrderAmount: response.count })
        this.props.updateOriginalOrders(response.transportOrders)
    }

    drawTable() {
        return <>
            {this.drawFilters()}
            <TransportOrderTable
                transportOrders={this.state.transportOrders}
                transportOrderAmount={this.state.transportOrderAmount}
                selectedOrderChanged={(newSelectedOrder) => { this.setState({ selectedTransportOrder: newSelectedOrder }) }}
                selectedOrderIdsChanged={(newSelectedOrderIds) => { this.setState({ selectedTransportOrderIds: newSelectedOrderIds }) }}
                reload={() => { }}
                initialPageSize={20}
                preSelectedTransportOrderIds={[]}
            />
        </>
    }

    drawScriptEditor() {
        return <>
            <AceEditor
                theme="monokai"
                mode='groovy'
                value={this.context.multiOrderScript}
                onChange={(v) => this.context.setMultiOrderScript(v)}
                placeholder='selected transportOrder'
                width="100%"
                height="100%"
                readOnly={false}
            />
        </>
    }

    drawSplittedArea() {
        return <div style={{ width: '100%', height: '100%' }}>
            <Splitter
                name="MultiOrderScriptEditSplitter"
                left={this.drawTable()}
                right={this.drawScriptEditor()}
            /></div>
    }

    async runScript() {

        this.setState({ isRunning: true })
        let auth = backend.withTokenAuthHeader(authentication.token)

        let scriptRequest = {} as MultiOrderScriptingRequest
        scriptRequest.transportOrderIds = this.state.selectedTransportOrderIds
        scriptRequest.groovyScript = this.context.multiOrderScript

        if (this.state.clientContext) {
            await this.runClientContextScript(scriptRequest, auth)
        } else {
            await this.runBackendContextScript(scriptRequest, auth)
        }
        this.setState({ isRunning: false })
    }

    async runClientContextScript(scriptRequest, auth) {
        let response = await fetch('/script-runner/executeMultiOrderScript', {
            method: 'POST',
            headers: {
                "Authorization": auth.headers.Authorization,
                "Content-Type": "application/json; charset=utf-8"
            },
            body: JSON.stringify(scriptRequest)
        })

        let jsonResponse = await response.json()
        this.props.updateXRequestId(response.headers.get("x-request-id"))
        this.context.setMultiOrderScriptResults(Object.values(jsonResponse.results))
    }

    async runBackendContextScript(scriptRequest, auth) {
        let response = await fetch('/api/internal/transportOrders/multiOrderScript', {
            method: 'POST',
            headers: {
                "Authorization": auth.headers.Authorization,
                "Content-Type": "application/json; charset=utf-8"
            },
            body: JSON.stringify(scriptRequest)
        })

        let jsonResponse = await response.json()
        this.props.updateXRequestId(response.headers.get("x-request-id"))
        this.context.setMultiOrderScriptResults(Object.values(jsonResponse.results))
    }

    render() {
        return <Form>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                <span>
                    Backend Context -
                    <Checkbox
                        style={{ margin: '0 6px' }}
                        type='checkbox'
                        checked={this.state.clientContext}
                        toggle
                        onChange={() => this.setState({ clientContext: !this.state.clientContext })}
                    />
                    - Client Context
                </span>
                <Button onClick={(evt) => this.runScript()} loading={this.state.isRunning}>Execute</Button>
            </div>

            <div
                ref={this.containerRef}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: 'calc(100vh - 150px)',
                    userSelect: this.state.isDragging ? 'none' : 'auto',
                }}
            >
                <div style={{ height: this.state.topHeight, flexShrink: 0, overflow: 'hidden' }}>
                    {this.drawSplittedArea()}
                </div>

                <div
                    onMouseDown={this.onDragStart}
                    style={{
                        height: 8,
                        cursor: 'row-resize',
                        background: this.state.isDragging ? '#bbb' : '#e0e0e0',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ width: 40, height: 2, background: '#999', borderRadius: 1 }} />
                </div>

                <div style={{ flex: 1, overflow: 'auto', paddingTop: 4 }}>
                    <h4 style={{ margin: '0 0 4px 0' }}>Logs:</h4>
                    <LogView
                        query={this.state.xRequestId}
                        origin={"multiScript"}
                        initialTimespan={null}
                        responseLogs={null}
                    />
                </div>
            </div>
        </Form>
    }
}